import { IPublicClientApplication } from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'
import { ThemeProvider } from '@mui/material/styles'
import { DatabaseProvider } from '@nozbe/watermelondb/react'
import * as Sentry from '@sentry/react'
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog'
import ErrorBoundary from 'components/ErrorBoundary'
import GenericErrorPage from 'components/errorPages/GenericErrorPage'
import Main from 'components/Main'
import { MinuteTimer } from 'components/MinuteTimer'
import WhatsNewStartupDialog from 'components/popups/userMenu/WhatsNewStartupDialog'
import AppModeProvider from 'components/providers/AppModeProvider'
import AppSettingsProvider from 'components/providers/AppSettingsProvider'
import { useAppSettings } from 'components/providers/AppSettingsProvider'
import AuthProvider from 'components/providers/AuthProvider'
import CalendarProvider from 'components/providers/CalendarProvider'
import {
  CalendarSyncProvider,
  useCalendarSync
} from 'components/providers/CalendarSyncProdivder'
import { ChatbaseProvider } from 'components/providers/ChatbaseProvider'
import ConnectionProvider from 'components/providers/ConnectionProvider'
import { doGetChanges } from 'components/providers/GetChangesProvider'
import { IcloudProvider } from 'components/providers/icloud/IcloudProvider'
import { NotificationProvider } from 'components/providers/NotificationProvider'
import ProvideOutlookContext, {
  useOutlookContext
} from 'components/providers/outlook/OutlookProvider'
import dayjs from 'dayjs'
import { getDefaultTimezone } from 'index'
import database from 'model'
import { ReactNode, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { handleAppBadge, updateUserSettings } from 'services/api/userSettings'
import Database from 'services/db/Database'
import {
  allCalendarItems,
  allPersonalCalendarItems,
  RemoveTempEvents
} from 'services/db/Queries/Event'
import { allGroups, RemoveTempGroups } from 'services/db/Queries/GroupQueries'
import { UserSettings } from 'services/db/Queries/UserSettingsQueries'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import QA from 'services/QA'
import { createAppTheme, theme } from 'theme'
import { TaskType, UserSettingsDoc } from 'types/api/changes'
import { LocalStorageKey } from 'types/services/localStorage'
import { is24meApp, isGroupcalApp } from 'utils/appType'
import { APP_COLOR } from 'utils/colors'
import {
  appStoreLink24me,
  appStoreLinkGroupcal,
  googlePlayLink24me,
  googlePlayLinkGroupcal
} from 'utils/device'
import { isTestEnv } from 'utils/env'
import { GENERIC_ERROR } from 'utils/strings'
import { isGuest } from 'utils/user'

QA.init()

const LOST_INDEXED_DB_CONNECTION_MESSAGE =
  'UnknownError: Connection to Indexed Database server lost. Refresh the page to try again'
const LOST_INDEXED_DB_RELOAD_KEY = 'lost-indexeddb-reload-attempted'

export const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV6(createBrowserRouter)

const router = sentryCreateBrowserRouter([
  {
    path: '/',
    element: (
      <ErrorBoundary>
        <AuthProvider>
          <Main />
        </AuthProvider>
      </ErrorBoundary>
    ),
    errorElement: <GenericErrorPage />
  },
  {
    path: '/calendar/:groupIdAndPass',
    element: (
      <ErrorBoundary>
        <AuthProvider>
          <Main />
        </AuthProvider>
      </ErrorBoundary>
    ),
    errorElement: <GenericErrorPage />
  },
  {
    path: '/calendars/',
    element: (
      <ErrorBoundary>
        <AuthProvider>
          <Main />
        </AuthProvider>
      </ErrorBoundary>
    ),
    errorElement: <GenericErrorPage />
  },
  {
    path: '/initiatepurchase',
    element: (
      <ErrorBoundary>
        <AuthProvider>
          <Main />
        </AuthProvider>
      </ErrorBoundary>
    ),
    errorElement: <GenericErrorPage />
  }
])

Sentry.init({
  dsn: 'https://728bb22830fe4d02890296b3b952bd8e@o4505345996357632.ingest.sentry.io/4505352289845248',
  enabled: process.env.REACT_APP_ENV !== 'TESTING',
  environment: `${process.env.REACT_APP_ENV_NAME}_${process.env.REACT_APP_APP_TYPE}`,
  release: process.env.APP_VERSION,

  // Suppress obvious browser-injected noise while keeping third-party SaaS errors visible.
  denyUrls: [
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-web-extension:\/\//i
  ],

  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
      maxReplayDuration: 600
    })
  ],

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: isTestEnv() ? 1 : 0.7,

  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0.0,
  replaysOnErrorSampleRate: 0.2,

  // Keep payload minimal but make triage easier in Sentry search/grouping.
  beforeSend(event) {
    const firstPartyHosts = new Set([
      'app.twentyfour.me',
      'app.groupcal.app',
      'localhost'
    ])

    const isFirstPartyFilename = (filename: string) => {
      if (!filename) return false

      // Keep local bundle frames classified as first-party even without hostnames.
      if (filename.startsWith('webpack://') || filename.startsWith('app:///')) {
        return true
      }

      try {
        const parsed = new URL(filename)
        return firstPartyHosts.has(parsed.hostname)
      } catch {
        return (
          filename.includes('localhost') ||
          filename.includes('app.twentyfour.me') ||
          filename.includes('app.groupcal.app')
        )
      }
    }

    const stackFrames =
      event.exception?.values?.flatMap(
        (value) => value.stacktrace?.frames ?? []
      ) ?? []

    const hasFirstPartyFrame = stackFrames.some((frame) =>
      isFirstPartyFilename(frame.filename ?? '')
    )

    event.tags = {
      ...event.tags,
      app_type: process.env.REACT_APP_APP_TYPE || 'unknown',
      guest_mode: window.location.pathname.includes('/calendar/') ? '1' : '0',
      error_source: hasFirstPartyFrame
        ? 'first_party_app'
        : 'third_party_script'
    }

    const hasChatbaseFrame =
      event.exception?.values?.some((value) =>
        value.stacktrace?.frames?.some((frame) =>
          frame.filename?.includes('chatbase.co')
        )
      ) ?? false

    if (hasChatbaseFrame) {
      event.tags = {
        ...event.tags,
        error_source: 'third_party_script',
        third_party_provider: 'chatbase'
      }
    }

    return event
  },

  // Benign race: Loki autosave setInterval fires while WatermelonDB is
  // closing/reopening the IDB connection (e.g. on guest embed cold-starts,
  // especially on iOS Safari inside 3rd-party iframes). The next autosave
  // succeeds — no data is lost.
  ignoreErrors: [
    /The database connection is closing/,
    /Failed to execute 'transaction' on 'IDBDatabase'/,
    /Cannot call database\.adapter\.underlyingAdapter while the database is being reset/,
    /Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node\./,
    /Cannot redefine property: webdriver/,
    /Object Not Found Matching Id:\d+, MethodName:update, ParamCount:\d+/,
    // Facebook/Meta in-app browser injects a JS performance logger that calls a
    // Java WebView bridge; by the time the call fires the Android Java object
    // has been GC'd. Our code is not involved.
    /Error invoking postMessage: Java object is gone/
  ]
})

function App(props: { pca: IPublicClientApplication | undefined }) {
  if (
    LocalStorage.get(LocalStorageKey.TIMEZONE) !==
    Intl.DateTimeFormat().resolvedOptions().timeZone
  ) {
    LocalStorage.set(
      LocalStorageKey.TIMEZONE,
      Intl.DateTimeFormat().resolvedOptions().timeZone
    )
  }

  // Set the CSS custom property based on the environment variable
  useEffect(() => {
    setupDefaultCssVariables()
  }, [])

  useEffect(() => {
    if (!isGuest()) {
      // for non guests ignore it
      return
    }

    const shouldRecoverLostIndexedDb = (message?: string) => {
      if (!message) return false
      if (message !== LOST_INDEXED_DB_CONNECTION_MESSAGE) return false
      if (!window.location.pathname.includes('/calendar/')) return false
      if (sessionStorage.getItem(LOST_INDEXED_DB_RELOAD_KEY) === '1') {
        return false
      }

      sessionStorage.setItem(LOST_INDEXED_DB_RELOAD_KEY, '1')
      Logger.orange('Recovering from lost IndexedDB connection with reload')
      window.location.reload()
      return true
    }

    const handleError = (event: ErrorEvent) => {
      const errorMessage = event.error?.message ?? event.message
      shouldRecoverLostIndexedDb(errorMessage)
    }

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason

      if (typeof reason === 'string') {
        shouldRecoverLostIndexedDb(reason)
        return
      }

      if (reason instanceof Error) {
        shouldRecoverLostIndexedDb(reason.message)
      }
    }

    window.addEventListener('error', handleError)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)

    return () => {
      window.removeEventListener('error', handleError)
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    }
  }, [])

  if (!LocalStorage.get(LocalStorageKey.SHOW_DEV_INFO)) {
    LocalStorage.set(LocalStorageKey.SHOW_DEV_INFO, '1')
  }

  const { t, i18n } = useTranslation()

  document.body.dir = i18n.dir()

  const [isMobileDevice, setIsMobileDevice] = useState(false)
  const [showDialog, setShowDialog] = useState(true)

  useEffect(() => {
    const userAgent = navigator.userAgent
    setIsMobileDevice(/Android|iPhone|iPad/i.test(userAgent))
    if (isMobileDevice) setShowDialog(/Android|iPhone|iPad/i.test(userAgent))

    handleAppBadge()

    //once app reloads
    LocalStorage.remove(LocalStorageKey.PENDING_EVENT_DATA)
    LocalStorage.remove(LocalStorageKey.VISIBLE_EVENT)
    LocalStorage.remove(LocalStorageKey.SHOULD_KEEP_POPUP)
    LocalStorage.remove(LocalStorageKey.VISIBLE_FROM)
    LocalStorage.remove(LocalStorageKey.VISIBLE_TO)
    LocalStorage.remove(LocalStorageKey.LANDLINE_DIALOG)

    RemoveTempEvents('App')
    RemoveTempGroups()

    updateTimezoneOffsetIfNeeded()
  }, [])

  const handleDialogClose = () => {
    setShowDialog(false)
  }

  const handleOpenAppStore = () => {
    window.open(is24meApp() ? appStoreLink24me : appStoreLinkGroupcal, '_blank')
  }

  const handleOpenGooglePlay = () => {
    window.open(
      is24meApp() ? googlePlayLink24me : googlePlayLinkGroupcal,
      '_blank'
    )
  }

  const userAgent = navigator.userAgent

  const isAndroid = /Android/i.test(userAgent)
  const isIOS = /iPhone|iPad|iPod/i.test(userAgent)

  const guestMode = location.pathname.includes('calendar')

  return (
    <AppSettingsProvider>
      <AppThemeProvider>
        {isMobileDevice && !guestMode ? (
          <div>
            {isMobileDevice && (
              <ConfirmationDialog
                title={t('toUseApp', {
                  appName: isGroupcalApp() ? t('appName') : t('appName24me')
                })}
                content={undefined}
                rightButton={`${t('downloadApp')}`}
                leftButton={`${t('close')}`}
                rightButtonColor={is24meApp() ? 'black' : APP_COLOR}
                leftButtonColor={theme.palette.input.placeholder}
                handleRightButton={
                  isAndroid ? handleOpenGooglePlay : handleOpenAppStore
                }
                handleLeftButton={handleDialogClose}
                open={showDialog}
              />
            )}
          </div>
        ) : (
          <ChatbaseProvider>
            <MsalProvider instance={props.pca!}>
              <AppModeProvider>
                <IcloudProvider>
                  <ProvideOutlookContext>
                    <DatabaseProvider database={database}>
                      <AppModule />
                    </DatabaseProvider>
                  </ProvideOutlookContext>
                </IcloudProvider>
              </AppModeProvider>
            </MsalProvider>
          </ChatbaseProvider>
        )}
        <AppSettingsErrorDialog />
      </AppThemeProvider>
    </AppSettingsProvider>
  )
}

function AppModule() {
  /**
   * We need to migrate old personal events to the new task type
   *
   */

  useEffect(() => {
    allPersonalCalendarItems().then((events) => {
      Database.write(async () => {
        for (const event of events) {
          if (event.TaskType !== '15') {
            await event.update((local) => {
              local.TaskType = TaskType.personalEvent // 15 is the task type for personal tasks
            })
          }
        }
      })
    })
  }, [])

  const MS_BETWEEN_SYNCS = 5000

  const outlookContext = useOutlookContext()
  const calendarSync = useCalendarSync()

  function performAppSync() {
    LocalStorage.set(LocalStorageKey.LAST_APP_SYNC, dayjs().valueOf())

    const deviceId = LocalStorage.get(LocalStorageKey.DEVICE_ID)

    if (deviceId && !isGuest())
      doGetChanges(
        deviceId,
        undefined,
        LocalStorage.get(LocalStorageKey.LAST_UPDATE),
        false,
        outlookContext
      )
    calendarSync.sync()
  }

  function onAppReceivedFocus() {
    const now = dayjs()
    const lastSync = dayjs.unix(
      Number(LocalStorage.get(LocalStorageKey.LAST_APP_SYNC)) / 1000
    )
    const lastGetChangesOnFocus = dayjs(
      Number(LocalStorage.get(LocalStorageKey.LAST_FOCUS_SYNC) ?? 0)
    )

    const SECONDS_FOR_GOOGLE_SYNC =
      Number(LocalStorage.get(LocalStorageKey.SYNC_INTERVAL)) || 600

    const millisDiff = now.diff(lastGetChangesOnFocus, 'millisecond')
    const secondsDiff = now.diff(lastSync, 'second')

    if (
      secondsDiff > SECONDS_FOR_GOOGLE_SYNC &&
      LocalStorage.get(LocalStorageKey.USER_ID)
    ) {
      performAppSync()
    }

    if (millisDiff > MS_BETWEEN_SYNCS) {
      LocalStorage.set(LocalStorageKey.LAST_FOCUS_SYNC, now.valueOf())

      const deviceId = LocalStorage.get(LocalStorageKey.DEVICE_ID)

      if (deviceId && LocalStorage.get(LocalStorageKey.USER_ID))
        doGetChanges(
          deviceId,
          undefined,
          LocalStorage.get(LocalStorageKey.LAST_UPDATE),
          false,
          outlookContext
        )
    }

    if (!LocalStorage.get(LocalStorageKey.SHOW_DEV_INFO)) {
      LocalStorage.set(LocalStorageKey.SHOW_DEV_INFO, 1)
    }
  }

  function onAppLostFocus() {
    let lastReload

    try {
      lastReload = dayjs.unix(
        Number(LocalStorage.get(LocalStorageKey.APP_RELOAD) ?? 0)
      )
    } catch (e) {
      //fix for https://24me.sentry.io/issues/4286962595/?project=4505352289845248&query=is%3Aunresolved&referrer=issue-stream&stream_index=0
      lastReload = dayjs()
    }

    const now = dayjs()
    const diff = now.diff(lastReload, 'hour')

    if (LocalStorage.get(LocalStorageKey.APP_RELOAD) === null || diff >= 12) {
      LocalStorage.set(LocalStorageKey.APP_RELOAD, dayjs().unix())
      window.location.reload()
    }
  }

  useEffect(() => {
    const handleVisibilityChange = () => {
      Logger.debug('tab in focus?', !document.hidden)

      if (!document.hidden) {
        onAppReceivedFocus()
      } else {
        onAppLostFocus()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  const SECONDS_FOR_GOOGLE_SYNC =
    Number(LocalStorage.get(LocalStorageKey.SYNC_INTERVAL)) || 600

  //to update google events
  useEffect(() => {
    const interval = setInterval(() => {
      Logger.debug('App re-sync due to timer')
      Logger.debug('is foreground?', !document.hidden)

      if (!document.hidden) {
        performAppSync()
      }
    }, SECONDS_FOR_GOOGLE_SYNC * 1000)

    return () => clearInterval(interval)
  }, [])

  return (
    <NotificationProvider>
      <CalendarSyncProvider>
        <ConnectionProvider>
          <CalendarProvider>
            <MinuteTimer>
              <></>
            </MinuteTimer>
            <RouterProvider router={router} />
          </CalendarProvider>
        </ConnectionProvider>
      </CalendarSyncProvider>
    </NotificationProvider>
  )
}

function AppThemeProvider(props: { children: ReactNode }) {
  const appSettings = useAppSettings()
  const appTheme = useMemo(
    () => createAppTheme(appSettings.resolvedThemeMode),
    [appSettings.resolvedThemeMode]
  )

  return <ThemeProvider theme={appTheme}>{props.children}</ThemeProvider>
}

function AppSettingsErrorDialog() {
  const { t } = useTranslation()
  const { isErrorDialogOpen, errorTitle, errorMessage, closeErrorDialog } =
    useAppSettings()

  if (!isErrorDialogOpen) return null

  return (
    <ConfirmationDialog
      open
      title={errorTitle ?? t('oopsError')}
      content={errorMessage ?? GENERIC_ERROR}
      handleRightButton={closeErrorDialog}
      handleClose={closeErrorDialog}
      leftButtonHidden
    />
  )
}

export default App

function setupDefaultCssVariables() {
  const root = document.documentElement

  root.style.setProperty('--app-accent-color', APP_COLOR) // Example color for twentyfourme
  root.style.setProperty(
    '--app-selected-color',
    is24meApp() ? 'black' : 'white'
  )
}
async function updateTimezoneOffsetIfNeeded() {
  if (isGuest()) return

  const userSettings = Database.toServerObject<UserSettingsDoc>(
    await UserSettings()
  ) as UserSettingsDoc

  if (
    userSettings.TimeZoneOffsetInSecFromGMT &&
    userSettings.TimeZoneOffsetInSecFromGMT ===
      (dayjs().utcOffset() * 60).toString()
  ) {
    Logger.debug('UserSettings: Timezone offset is up to date')
    return
  }

  userSettings.TimeZoneOffsetInSecFromGMT = (
    dayjs().utcOffset() * 60
  ).toString()

  Logger.orange('UserSettings:Updating offset', userSettings)
  await updateUserSettings(userSettings)
}
